import { fetchCrudo } from './platform';
import { fetchConRecuperacionSesionIA } from './recuperacionSesionIA';
import { getOrCreateSessionId } from '../utils/session';
import { PROMPT_VERSION } from '../utils/appVersion';
import { modelForCapa, modeloAceptaSampling, thinkingParaModelo } from './aiConfig';
import { bloquesCacheDiagnosticos } from './cacheDiagnosticos';

// En web: '' (relativo). En Capacitor: REACT_APP_API_URL=https://mipae.vercel.app
const API_BASE = process.env.REACT_APP_API_URL || '';

// Ajusta el body antes de enviarlo al endpoint:
//  1. Ruteo de modelo. Si el caller pasa `modelo` explícito (routing por mensaje,
//     ej. Benito → Haiku/Sonnet según la consulta) ese gana; si no, lo decide la
//     capa (ver aiConfig.modelForCapa); y si no hay capa, queda el body.model del
//     caller como fallback para no romper callers antiguos.
//  2. Cache del system prompt: si viene como string, se envuelve en un bloque
//     con cache_control para servir su prefijo cacheado (~0.1x) en llamadas
//     repetidas. Prefijos bajo el mínimo del modelo simplemente no cachean, sin
//     costo extra. Si el caller ya manda system como array, se respeta tal cual.
//  3. Contrato de la familia 5 (ver aiConfig): se saca el sampling que Sonnet 5
//     rechaza y se apaga el thinking que trae prendido por defecto. Va acá, con
//     el modelo YA resuelto por capa, porque el modelo del call-site miente: el
//     PAE manda `model: AI_MODEL_ID` (Haiku) y es la capa la que lo sube a
//     Sonnet. Normalizar en el call-site habría dejado los cuatro sitios que
//     mandan temperature con un 400 silencioso.
function prepararBody(body, capa, modelo) {
  const out = { ...body };
  if (modelo) out.model = modelo;
  else if (capa) out.model = modelForCapa(capa);
  // Solo el diagnóstico formal en su modelo validado. Los callers que ya
  // eligen breakpoints, TTL o herramientas conservan íntegra su política.
  // La guarda de mensajes evita consumir otro breakpoint si el caller ya
  // marcó contenido (Anthropic permite cuatro por request).
  const mensajesConCache = out.messages?.some(m => Array.isArray(m.content)
    && m.content.some(b => b?.cache_control));
  if (capa === 'diagnosticos' && out.model === 'claude-sonnet-4-6'
      && !out.cache_control && !out.tools && !mensajesConCache) {
    out.system = bloquesCacheDiagnosticos(out.system) || out.system;
  }
  if (typeof out.system === 'string' && out.system.trim()) {
    // El system HEREDA el cache_control top-level del request cuando existe.
    // OJO con la regla que hay detrás, porque este comentario la tuvo al revés
    // y eso costó plata: la API exige que las entradas de TTL más LARGO
    // aparezcan ANTES que las de 5 min en el mismo request, y `system` se
    // renderiza ANTES que `messages`. O sea system 1h + cola 5 min es el orden
    // VÁLIDO; lo que da 400 es el revés. La herencia no era obligatoria, y
    // aplicarla puso el TTL de 1 h también sobre la cola conversacional, que se
    // re-escribe en cada vuelta del loop: escrituras a 2x en vez de 1.25x.
    //
    // Hoy Benito manda su system YA como array con su propio breakpoint de 1 h
    // (ver el TTL partido en lib/agente.js), así que cae en la rama de abajo y
    // esta herencia no lo toca. Se conserva como red para cualquier caller que
    // mande system en string junto a un top-level de TTL largo: ahí heredar
    // sigue siendo lo correcto, porque un system de 5 min DESPUÉS de un
    // top-level de 1 h sí sería el orden inválido. Sin top-level (el resto de
    // las capas), queda el ephemeral de 5 min de siempre.
    out.system = [{ type: 'text', text: out.system, cache_control: { ...(out.cache_control || { type: 'ephemeral' }) } }];
  }
  if (!modeloAceptaSampling(out.model)) {
    delete out.temperature;
    delete out.top_p;
    delete out.top_k;
  }
  const thinking = thinkingParaModelo(out.model);
  // Un caller que fije `thinking` explícito manda: la normalización solo cubre
  // el default, no pisa una decisión deliberada.
  if (thinking && out.thinking === undefined) out.thinking = thinking;
  return out;
}

// opts: { capa: 'diagnosticos' | ..., paeId?: string, modulo?: 'pae_rapido' | 'pae_detallado', evolucionRef?: string }
// `modulo` distingue las dos generaciones de diagnósticos (misma capa): la
// cuota del PAE Detallado se cuenta por él (api/_shared/aiCalls.js,
// featuresCuotaDeLlamada). Sin él, el Detallado no se cuenta como Detallado.
// Las cabeceras X-MiPAE-* alimentan el logger ai_calls del lado server.
// Capa es opcional aquí (el server cae a 'otro' si falta) para no romper
// callers DEPRECATED, pero todo callsite vivo debería pasarla.
export async function callClaude(body, signal, opts = {}) {
  const headers = {
    'Content-Type': 'application/json',
    'X-MiPAE-Session-Id': getOrCreateSessionId(),
    'X-MiPAE-Prompt-Version': PROMPT_VERSION,
  };
  if (opts.capa) headers['X-MiPAE-Capa'] = opts.capa;
  if (opts.paeId) headers['X-MiPAE-PAE-Id'] = opts.paeId;
  if (opts.modulo) headers['X-MiPAE-Modulo'] = opts.modulo;
  // Paciente-turno de la evolución con IA (id local del registro, sin datos
  // del paciente): el servidor cuenta con él la evolución semanal gratis.
  if (opts.evolucionRef) headers['X-MiPAE-Evolucion-Ref'] = opts.evolucionRef;
  return fetchConRecuperacionSesionIA(`${API_BASE}/api/claude`, {
    method: 'POST', signal, headers, body: JSON.stringify(prepararBody(body, opts.capa, opts.modelo)),
  }, (...args) => fetch(...args));
}

// Variante streaming de callClaude. Pide stream:true y consume el SSE de
// Anthropic que el server (api/claude.js) reenvía tal cual. Devuelve el TEXTO
// completo acumulado; onChunk(chunk, fullText) se invoca por cada delta para
// feedback incremental. El pipeline de diagnóstico necesita el texto completo
// para procesar, pero streamear evita el muro de 25s del server: Anthropic
// emite tokens de inmediato en vez de esperar la respuesta entera.
//
// Si el server responde JSON (error o rate-limit) en vez de SSE, se lanza Error
// con el mensaje del server — mismo contrato de error que el caller espera.
export async function callClaudeStreamText(body, signal, opts = {}, onChunk = null) {
  const senales = await callClaudeStream(body, signal, opts, onChunk);
  const err = errorDeSenales(senales);
  if (err) throw err;
  return senales.texto;
}

// Cómo terminó el stream, en un error con `codigo` de catálogo cerrado, o null
// si terminó bien. Un corte por `max_tokens` NO se convierte en error aquí a
// propósito: el reparto de la evolución lo decide por su cuenta (reintenta con
// más aire), y los consumidores del PAE conservan su comportamiento de siempre
// en este lote (su validación clínica está calibrada contra lo que reciben).
export function errorDeSenales({ errorEvento, terminado, stopReason }) {
  if (errorEvento) {
    const err = new Error('El servicio de IA falló a mitad de la respuesta. Lo tuyo sigue intacto; intenta de nuevo en un momento.');
    err.codigo = 'proveedor';
    err.tipo = errorEvento.tipo;
    return err;
  }
  if (!terminado && !stopReason) {
    const err = new Error('Se cortó la conexión antes de terminar la respuesta. Lo tuyo sigue intacto; intenta de nuevo.');
    err.codigo = 'stream_incompleto';
    return err;
  }
  return null;
}

// Lector SSE con SEÑALES (auditoría del motor 2026-09-05, hallazgos H-E1/G-01):
// devuelve el texto acumulado Y cómo terminó el stream, para que el llamador
// distinga éxito de corte, error del proveedor y cierre a medias. Hasta hoy el
// cliente acumulaba text_delta y devolvía lo que hubiera: un `overloaded_error`
// a mitad del stream volvía como respuesta válida a medias, y la única red era
// que el JSON no parseara.
//   texto        → lo acumulado de content_block_delta/text_delta
//   stopReason   → message_delta.delta.stop_reason ('end_turn', 'max_tokens'…)
//   terminado    → llegó message_stop (fin válido del proveedor o del respaldo)
//   errorEvento  → { tipo, mensaje } si el proveedor emitió un evento `error`
// Un stream cerrado sin message_stop ni stop_reason NO es éxito: lo decide el
// llamador (errorDeSenales). El respaldo OpenAI emite ambos (armarSseRespaldo).
export async function callClaudeStream(body, signal, opts = {}, onChunk = null) {
  const headers = {
    'Content-Type': 'application/json',
    'X-MiPAE-Session-Id': getOrCreateSessionId(),
    'X-MiPAE-Prompt-Version': PROMPT_VERSION,
  };
  if (opts.capa) headers['X-MiPAE-Capa'] = opts.capa;
  if (opts.paeId) headers['X-MiPAE-PAE-Id'] = opts.paeId;
  if (opts.modulo) headers['X-MiPAE-Modulo'] = opts.modulo;
  // Paciente-turno de la evolución con IA (id local del registro, sin datos
  // del paciente): el servidor cuenta con él la evolución semanal gratis.
  if (opts.evolucionRef) headers['X-MiPAE-Evolucion-Ref'] = opts.evolucionRef;

  // fetchCrudo y no fetch: en la app instalada el parche de CapacitorHttp
  // bufferea la respuesta entera (Benito aparecía de golpe al final, no palabra
  // a palabra) y descarta el signal (Detener no detenía nada). Ver
  // lib/platform.js. La llamada sin stream de arriba sigue por el parche: para
  // JSON funciona y en Android lleva un mes en producción.
  const res = await fetchConRecuperacionSesionIA(`${API_BASE}/api/claude`, {
    method: 'POST',
    signal,
    headers,
    body: JSON.stringify({ ...prepararBody(body, opts.capa, opts.modelo), stream: true }),
  }, fetchCrudo);

  // El server cae a JSON ante error/rate-limit aunque se pidiera stream.
  const contentType = res.headers.get('content-type') || '';
  if (contentType.includes('application/json')) {
    const data = await res.json().catch(() => ({}));
    const msg = data?.error?.message || data?.error || `Error API (${res.status})`;
    // El `codigo` de catálogo del 403 de cuota (cuota_pae, cuota_detallado,
    // tope_benito…) viaja en el error: quien llama distingue un límite del
    // plan (se avisa con calma y se conserva lo que había) de una falla.
    const err = new Error(msg);
    if (typeof data?.codigo === 'string') err.codigo = data.codigo;
    throw err;
  }
  if (!res.ok || !res.body) {
    throw new Error(`Error API (${res.status})`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let fullText = '';
  let stopReason = null;
  let terminado = false;
  let errorEvento = null;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() || '';
    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      const data = line.slice(6).trim();
      if (!data || data === '[DONE]') continue;
      try {
        const ev = JSON.parse(data);
        if (ev.type === 'content_block_delta' && ev.delta?.type === 'text_delta') {
          const chunk = ev.delta.text || '';
          fullText += chunk;
          if (onChunk) onChunk(chunk, fullText);
        } else if (ev.type === 'message_delta' && ev.delta?.stop_reason) {
          stopReason = ev.delta.stop_reason;
        } else if (ev.type === 'message_stop') {
          terminado = true;
        } else if (ev.type === 'error') {
          // Solo el TIPO se usa (código de catálogo en errorDeSenales y
          // telemetría). El mensaje del proveedor se conserva en la señal para
          // depurar en consola, pero no se muestra ni se traza: el texto en
          // pantalla es siempre el nuestro.
          errorEvento = { tipo: ev.error?.type || 'error', mensaje: ev.error?.message || '' };
        }
      } catch {
        // Línea SSE malformada — ignorar.
      }
    }
  }

  return { texto: fullText, stopReason, terminado, errorEvento };
}
